import { FC, useLayoutEffect, useState } from "react";
import ActionButtons, {
    IActionButton,
    styleColorButton,
} from "../../../assets/components/ActionButtons/ActionButtons";
import { OneLicense } from "../model";
import "./LicenseKeysSubRow.scss";
import {
    NameStatusLicense,
    getStatusData,
    detectCurrentNameStatus,
} from "../helpers";
import {
    useDeactivateLicenseMutation,
    useExpireLicenseMutation,
    useGetHistoryLicenseQuery,
    useGracePeriodMutation,
} from "../../../redux/RTKQuery/api";
import { LicenseKeysLicenseActivations } from "./LicenseKeysLicenseActivations/LicenseKeysLicenseActivations";
import { hasPermission } from "../../../assets/helpers/helpers";
import { useAppDispatch, useAppSelector } from "../../../assets/hooks/hooks";
import { AccessRoles, Permissions } from "../../../models/models";
import { useNotification } from "../../../assets/hooks/useNotification";
import { actionText } from "../../../text";
import LicenseKeysInformPanel, {
    LicenseInfoPanel,
} from "./InformPanel/LicenseKeysInformPanel";
import LicenseKeyHistory from "../LicenseKeyHistory/LicenseKeyHistory";
import { FetchLicenseHistory } from "../LicenseKeyHistory/historyModel";
import { toast } from "react-toastify";
import { uiSlicedActions } from "../../../redux/ui-slice";

interface IConfigButton {
    name: string;
    color: styleColorButton;
    onClick: () => void;
    disabled: boolean;
}

const LicenseKeysSubRow: FC<OneLicense> = ({ license, user }) => {
    const dispatch = useAppDispatch();
    const [expireLicense] = useExpireLicenseMutation();
    const [gracePeriod] = useGracePeriodMutation();
    const [deactivateLicense] = useDeactivateLicenseMutation();
    const { ConfirmationMessageBox } = useNotification();

    const [licenseHistory, setData] = useState<FetchLicenseHistory[]>([]);

    const [licenceInfo] = useState<LicenseInfoPanel>({
        expirationDate: license.activations[0]?.expirationDate,
        activationDate: license.activations[0]?.activationDate,
        type: license.type,
        resellerName: user.resellerName,
        status: license.status,
        migrationKeyId: license.migrationKeyId,
        orderId: license.orderId,
        reason: license.reason,
    });

    const {
        data: licenceHistory,
        isLoading: isLoadingLicenceHistory,
        isSuccess: isSuccessLicenceHistory,
        isError: isErrorLicenceHistory,
    } = useGetHistoryLicenseQuery({
        licenseKey: license.key,
    });

    dispatch(
        uiSlicedActions.setLoading(
            isLoadingLicenceHistory ? "pending" : "hidden",
        ),
    );

    useLayoutEffect(() => {
        if (isSuccessLicenceHistory && licenceHistory) {
            setData(licenceHistory);
        }
        if (isErrorLicenceHistory) {
            toast.error("Error fetching license history");
        }
    }, [licenceHistory, isSuccessLicenceHistory, isErrorLicenceHistory]);

    const userAuth = useAppSelector((state) => state.initializeApp);
    const [licenseStatus, setLicenseStatus] = useState<number>(license.status);

    const [configButton, setConfigButton] = useState<IConfigButton>({
        name: "",
        color: "blue",
        onClick: () => {},
        disabled: false,
    });

    const gracePeriodHandler = () => {
        ConfirmationMessageBox({
            textContent: {
                title: `Are you sure you wish to change the license status?`,
            },
        }).then((value) => {
            if (value === "yes") gracePeriod({ licenseKey: license.key });
        });
    };

    const expireHandler = () =>
        ConfirmationMessageBox({
            textContent: actionText("expire", "licence"),
        }).then((value) => {
            if (value === "yes") expireLicense(license.key);
        });

    const deactivateHandler = () =>
        ConfirmationMessageBox({
            textContent: actionText("deactivate", "licence"),
        }).then((value) => {
            if (value === "yes")
                deactivateLicense({
                    key: license.key,
                    state: false,
                });
        });

    const activatedHandler = () =>
        ConfirmationMessageBox({
            textContent: actionText("activate", "licence"),
        }).then((value) => {
            if (value === "yes")
                deactivateLicense({
                    key: license.key,
                    state: true,
                });
        });

    useLayoutEffect(() => {
        setLicenseStatus(license.status);
        switch (getStatusData(licenseStatus)?.name) {
            case NameStatusLicense.NOT_ACTIVATED:
                setConfigButton({
                    name: "Activated",
                    color: "green",
                    onClick: () => {},
                    disabled: true,
                });
                break;
            case NameStatusLicense.ACTIVATED:
                setConfigButton({
                    name: "Deactivate",
                    color: "red",
                    disabled: false,
                    onClick: () => deactivateHandler(),
                });
                break;
            case NameStatusLicense.GRACE_PERIOD:
                setConfigButton({
                    name: "Deactivate",
                    color: "red",
                    disabled: false,
                    onClick: () => deactivateHandler(),
                });
                break;
            case NameStatusLicense.DEACTIVATED:
                setConfigButton({
                    name: "Reset",
                    color: "green",
                    disabled: false,
                    onClick: () => activatedHandler(),
                });
                break;
            case NameStatusLicense.EXPIRED:
                setConfigButton({
                    name: "Deactivate",
                    color: "green",
                    disabled: true,
                    onClick: () => {},
                });
                break;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [license.key, license.status, licenseStatus]);

    const isSupport = userAuth.role === AccessRoles.SUPPORT;

    const supportActionButtons: IActionButton[] = [
        {
            styling: { type: "advanced", color: "purple" },
            type: "button",
            name: "Grace Period",
            role: "secondary",
            disabled: detectCurrentNameStatus(license.status, [
                NameStatusLicense.EXPIRED,
                NameStatusLicense.NOT_ACTIVATED,
                NameStatusLicense.GRACE_PERIOD,
                NameStatusLicense.DEACTIVATED,
            ]),
            onClick: () => gracePeriodHandler(),
        },
        {
            styling: { type: "advanced", color: "yellow" },
            type: "button",
            name: "Expired",
            role: "secondary",
            disabled: detectCurrentNameStatus(license.status, [
                NameStatusLicense.EXPIRED,
                NameStatusLicense.NOT_ACTIVATED,
            ]),
            onClick: () => expireHandler(),
        },
    ];

    const actionButtons: IActionButton[] = isSupport
        ? supportActionButtons
        : [
              ...supportActionButtons,
              {
                  styling: { type: "advanced", color: configButton.color },
                  type: "button",
                  role: "secondary",
                  name: configButton.name,
                  disabled: configButton.disabled,
                  onClick: configButton.onClick,
              },
          ];

    return (
        <div className="key_checker">
            <div className="key_checker__container">
                <div className="key_checker__col">
                    <div className="row">
                        <LicenseKeysInformPanel info={licenceInfo} />
                    </div>
                    <div className="row">
                        {license.activations.length ? (
                            <LicenseKeysLicenseActivations
                                activationList={license.activations}
                                allowedMachineActivationCount={
                                    license.allowedMachineActivationCount
                                }
                                licenseKey={license.key}
                                licenseId={license.id}
                            />
                        ) : null}
                    </div>
                </div>
            </div>

            {(hasPermission(
                userAuth.permissions,
                Permissions.LICENSE_EDIT,
            ) ||
                user.id === userAuth.userId) && (
                <div className="key_checker__footer">
                    <ActionButtons
                        className="form__button_submit"
                        actionButtons={actionButtons}
                    />
                </div>
            )}

            <LicenseKeyHistory licenseHistory={licenseHistory} />
        </div>
    );
};

export default LicenseKeysSubRow;
